/**
 * Preview-only registry of custom fields added at runtime.
 *
 * In Lovable preview mode there's no backend, so custom columns created
 * via the column manager only live in per-table localStorage. This module
 * adds a workspace-scoped registry so a custom field created on any
 * pipeline / list also appears in the workspace Deal Settings "New Deal
 * Fields" picker (and any other surface that lists available custom
 * fields per object type).
 */

export type PreviewCustomObjectType = "Contact" | "Account" | "Deal";

export interface PreviewCustomFieldEntry {
  id: string;       // key used by the table column (e.g. "custom_xxx")
  label: string;
  name: string;     // backend-style name
}

const EVENT_NAME = "preview-custom-fields:change";

function storageKey(workspaceId: string | null, objectType: PreviewCustomObjectType) {
  const ws = workspaceId || "default";
  return `ws_${ws}__preview-custom-fields:${objectType}`;
}

export function readPreviewCustomFields(
  workspaceId: string | null,
  objectType: PreviewCustomObjectType
): PreviewCustomFieldEntry[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(storageKey(workspaceId, objectType));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function registerPreviewCustomField(
  workspaceId: string | null,
  objectType: PreviewCustomObjectType,
  entry: PreviewCustomFieldEntry
) {
  if (typeof window === "undefined") return;
  const existing = readPreviewCustomFields(workspaceId, objectType);
  if (existing.some(e => e.id === entry.id)) return;
  const next = [...existing, entry];
  try {
    window.localStorage.setItem(storageKey(workspaceId, objectType), JSON.stringify(next));
    window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: { objectType } }));
  } catch {
    // Ignore quota / serialization errors
  }
}

export function subscribePreviewCustomFields(handler: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  window.addEventListener(EVENT_NAME, handler);
  return () => window.removeEventListener(EVENT_NAME, handler);
}

export function updatePreviewCustomField(
  workspaceId: string | null,
  objectType: PreviewCustomObjectType,
  id: string,
  patch: Partial<Omit<PreviewCustomFieldEntry, "id">>
) {
  if (typeof window === "undefined") return;
  const existing = readPreviewCustomFields(workspaceId, objectType);
  const next = existing.map(e => (e.id === id ? { ...e, ...patch } : e));
  try {
    window.localStorage.setItem(storageKey(workspaceId, objectType), JSON.stringify(next));
    window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: { objectType } }));
  } catch { /* ignore */ }
}

export function removePreviewCustomField(
  workspaceId: string | null,
  objectType: PreviewCustomObjectType,
  id: string
) {
  if (typeof window === "undefined") return;
  const existing = readPreviewCustomFields(workspaceId, objectType);
  const next = existing.filter(e => e.id !== id);
  try {
    window.localStorage.setItem(storageKey(workspaceId, objectType), JSON.stringify(next));
    window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: { objectType } }));
  } catch { /* ignore */ }
}